import { Group, Text } from '@mantine/core'
import { IconLock } from '@tabler/icons-react'
import { EmptyState } from '@lighthouse/design'
import { ADMIN_ROLE_REQUIRED } from '../../utils/format'

/**
 * What a Lighthouse.Admin surface shows a caller the server says lacks the role.
 *
 * Neutral, not red: a missing role is a state to explain, not a fault — painting
 * it as an error teaches operators that the page is broken. The server's own
 * reason is quoted when it gave one, because it says what to do next.
 *
 * CONTRACT for admin pages (see hooks/useLighthouseAdmin.ts): render this on gate
 * `denied` WITHOUT fetching, and also when the page's own query fails with
 * errorKind 'forbidden'. `compact` is the Overview preview-card form.
 */
export function RestrictedState({
  surface, reason, compact = false,
}: {
  surface: string
  reason?: string | null
  compact?: boolean
}) {
  const said = reason?.trim() ? reason.trim() : null

  if (compact) {
    return (
      <Group gap={6} wrap="nowrap" align="flex-start" role="status">
        <IconLock size={14} stroke={1.8} style={{ flexShrink: 0, marginTop: 2, color: 'var(--color-text-disabled)' }} />
        <Text size="xs" c="dimmed">
          {ADMIN_ROLE_REQUIRED}.{said && <> The server says: {said}</>}
        </Text>
      </Group>
    )
  }

  return (
    <div role="status">
      <EmptyState
        icon={<IconLock size={28} stroke={1.5} />}
        title={ADMIN_ROLE_REQUIRED}
        message={
          <>
            {surface} is served only to holders of Lighthouse.Admin.
            {said && <> The server says: {said}</>}
          </>
        }
      />
    </div>
  )
}
